import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Sparkles, Flame, Calendar, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="rounded-2xl border border-border p-6 bg-card/40 backdrop-blur-sm">
    <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
    </div>
    <h3 className="font-heading font-medium mb-1">{title}</h3>
    <p className="text-sm text-muted-foreground">{desc}</p>
  </div>
);

export default function Landing() {
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen flex flex-col" data-testid="landing-page">
      <header className="flex items-center justify-between px-6 lg:px-12 py-6 pt-safe relative z-10">
        <div className="text-primary"><Logo size={48} /></div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} className="p-2 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Toggle theme" data-testid="landing-theme-toggle">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="landing-login-btn">Log in</Button></Link>
          <Link to="/signup"><Button className="rounded-full" data-testid="landing-signup-btn">Get started</Button></Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto max-w-5xl px-6 lg:px-12 pt-12 lg:pt-20 pb-16 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium mb-6">A calmer way to build habits</p>
            <h1 className="font-heading font-semibold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              The habit tracker that fits into your <span className="text-primary">flow</span>, not the other way around.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Check off rituals, count glasses of water, run a focused timer — three habit types in one elegant interface, with streaks, heatmaps, and a calming dark mode that&apos;s actually been designed.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-7" data-testid="hero-cta-signup">
                  Start free — no credit card
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="rounded-full" data-testid="hero-cta-login">
                  I already have an account
                </Button>
              </Link>
            </div>
          </motion.div>
        </section>

        <section className="mx-auto max-w-5xl px-6 lg:px-12 pb-20 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Feature icon={Sparkles} title="Any habit, any shape" desc="Binary check-ins, quantitative goals, or time-based timers — all in one flow." />
            <Feature icon={Flame} title="Streaks that motivate" desc="Smart streak logic with skip-with-reason so a tough day doesn't reset your progress." />
            <Feature icon={Calendar} title="Beautiful heatmaps" desc="GitHub-style yearly views — see your story unfold one square at a time." />
            <Feature icon={BarChart3} title="Insightful stats" desc="Completion rates, best days of the week, monthly trends — clean and honest." />
          </div>
        </section>
      </main>

      <footer className="relative z-10">
        <Footer />
      </footer>
    </div>
  );
}
